import { useEffect, useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ConfirmationModal } from "@/components/ui/confirmation-modal";
import { Trash2 } from "lucide-react";
import { formatDate } from "@/utils/formatters";
import {
  adminEmailAutomationsService,
  type MilestoneConfigurationRule,
  type AutomationMetric,
} from "@/services/admin/milestoneConfiguration.admin.service";

interface FormState {
  type: AutomationMetric | "";
  threshold: string;
  isActive: boolean;
}

const metricOptions: { label: string; value: AutomationMetric }[] = [
  { label: "Affiliate count", value: "AFFILIATES" },
  { label: "Total Revenue", value: "REVENUE" },
];

export default function MilestoneConfiguration() {
  const [rules, setRules] = useState<MilestoneConfigurationRule[]>([]);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [form, setForm] = useState<FormState>({
    type: "AFFILIATES",
    threshold: "",
    isActive: true,
  });

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      const items = await adminEmailAutomationsService.list();
      setRules(items);
      setIsLoading(false);
    };
    load();
  }, []);

  const grouped = useMemo(() => {
    return {
      AFFILIATES: rules
        .filter((r) => r.type === "AFFILIATES")
        .sort((a, b) => a.threshold - b.threshold),
      REVENUE: rules
        .filter((r) => r.type === "REVENUE")
        .sort((a, b) => a.threshold - b.threshold),
    } as Record<AutomationMetric, MilestoneConfigurationRule[]>;
  }, [rules]);

  const resetForm = () =>
    setForm({
      type: "AFFILIATES",
      threshold: "",
      isActive: true,
    });

  const isFormValid = form.type && form.threshold && Number(form.threshold) > 0;

  const handleCreate = async () => {
    if (!isFormValid) return;
    setSaving(true);
    try {
      const payload = {
        type: form.type as AutomationMetric,
        threshold: Number(form.threshold),
        isActive: form.isActive,
      };
      const created = await adminEmailAutomationsService.create(payload);
      setRules((prev) => [created, ...prev]);
      resetForm();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await adminEmailAutomationsService.remove(id);
      setRules((prev) => prev.filter((r) => r.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<MilestoneConfigurationRule | null>(null);

  const handleToggleActive = async (id: string, isActive: boolean) => {
    setTogglingId(id);
    try {
      const updated = await adminEmailAutomationsService.update(id, {
        isActive,
      });
      setRules((prev) => prev.map((r) => (r.id === id ? updated : r)));
    } finally {
      setTogglingId(null);
    }
  };

  const buildColumns = (
    metric: AutomationMetric,
  ): ColumnDef<MilestoneConfigurationRule>[] => [
    {
      accessorKey: "threshold",
      header: metric === "REVENUE" ? "Revenue threshold" : "Affiliate count",
      meta: { width: "w-[30%]" },
      cell: ({ row }) => (
        <span className="font-medium text-foreground">
          {metric === "REVENUE"
            ? `$${row.original.threshold.toLocaleString()}`
            : row.original.threshold.toLocaleString()}
        </span>
      ),
    },
    {
      accessorKey: "updatedAt",
      header: "Last updated",
      meta: { width: "w-[26%]" },
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {row.original.updatedAt ? formatDate(row.original.updatedAt) : "—"}
        </span>
      ),
    },
    {
      id: "active",
      header: "Active",
      meta: { width: "w-[28%]" },
      cell: ({ row }) => (
        <div className="inline-flex items-center gap-2 text-xs text-muted-foreground">
          <Switch
            checked={row.original.isActive}
            onCheckedChange={(val: boolean) =>
              handleToggleActive(row.original.id, val)
            }
            disabled={togglingId === row.original.id}
          />
          {togglingId === row.original.id ? (
            <span className="flex items-center gap-1">
              <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-muted-foreground" />
              {row.original.isActive ? "Deactivating..." : "Activating..."}
            </span>
          ) : row.original.isActive ? (
            <span className="text-green-600 dark:text-green-400 font-medium">
              Active
            </span>
          ) : (
            <span>Inactive</span>
          )}
        </div>
      ),
    },
    {
      id: "actions",
      header: () => <span className="block text-right">Actions</span>,
      meta: { width: "w-20", cellClassName: "text-right" },
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDeleteTarget(row.original)}
          disabled={deletingId === row.original.id}
          className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/10"
        >
          {deletingId === row.original.id ? (
            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Create milestone</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Select
                value={form.type}
                onValueChange={(val) =>
                  setForm((f) => ({ ...f, type: val as AutomationMetric }))
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select metric" />
                </SelectTrigger>
                <SelectContent>
                  {metricOptions.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Threshold value"
                value={form.threshold}
                onKeyDown={(e) => {
                  const allowed = [
                    "Backspace",
                    "Delete",
                    "ArrowLeft",
                    "ArrowRight",
                    "Tab",
                    "Home",
                    "End",
                  ];
                  if (allowed.includes(e.key)) return;
                  if (!/^[0-9]$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => (e.currentTarget as HTMLInputElement).blur()}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setForm((f) => ({
                    ...f,
                    threshold: e.target.value.replace(/[^0-9]/g, ""),
                  }))
                }
                className="w-full"
              />
            </div>
            <div className="flex items-center gap-3 h-9">
              <Switch
                className="data-[state=checked]:bg-[#d7785b] data-[state=checked]:hover:bg-[#c66a4f] text-white"
                checked={form.isActive}
                onCheckedChange={(val: boolean) =>
                  setForm((f) => ({ ...f, isActive: val }))
                }
              />
              <span className="text-sm text-muted-foreground">Active</span>
            </div>
            <Button
              className="bg-[#d7785b] hover:bg-[#c66a4f] w-32 text-white"
              disabled={!isFormValid || saving}
              onClick={handleCreate}
            >
              {saving ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </div>
              ) : (
                "Create milestone"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="gap-0 py-0 overflow-hidden">
          <CardHeader className="border-b border-border px-5 py-4 md:px-6">
            <CardTitle className="text-lg font-semibold text-foreground">
              Affiliate count milestones
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <DataTable
              columns={buildColumns("AFFILIATES")}
              data={grouped.AFFILIATES}
              isLoading={isLoading}
              skeletonRows={4}
              getRowId={(row) => row.id}
              emptyText="No milestones yet"
            />
          </CardContent>
        </Card>
        <Card className="gap-0 py-0 overflow-hidden">
          <CardHeader className="border-b border-border px-5 py-4 md:px-6">
            <CardTitle className="text-lg font-semibold text-foreground">
              Revenue milestones
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <DataTable
              columns={buildColumns("REVENUE")}
              data={grouped.REVENUE}
              isLoading={isLoading}
              skeletonRows={4}
              getRowId={(row) => row.id}
              emptyText="No milestones yet"
            />
          </CardContent>
        </Card>
      </div>

      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => {
          if (!deletingId) setDeleteTarget(null);
        }}
        onConfirm={() => {
          if (deleteTarget) {
            const id = deleteTarget.id;
            setDeleteTarget(null);
            void handleDelete(id);
          }
        }}
        title="Delete milestone"
        message="Are you sure you want to delete this milestone? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        isLoading={!!deletingId}
      />
    </div>
  );
}

